// Смарт-процессы Bitrix: правила показа, общие для всех типов.
//
// Почему здесь нет ни одного кода UF-поля. У дебиторки (134) состав полей выяснен и зашит
// в lib/payments.ts — там это оправдано: экран считает по ним долг, и ошибиться в коде нельзя.
// У остальных типов (31, 153, 170, 176, 1160) состав полей свой, он меняется в Bitrix без нас,
// и придумать коды «по смыслу» — это молча показать пустые колонки. Поэтому колонки строятся
// из справочника `/fields` (`item:<тип>`): оттуда берутся и название, и тип, и значения
// перечислений, и пометка «поле считает бизнес-процесс».
//
// Здесь только правила («поле типа money показываем деньгами и суммируем»), разметки нет —
// она в components/SmartItemsTable.tsx.

import type { FieldsApi } from '../api/hooks'
import type { CrmItem, FieldMeta } from '../api/types'
import type { EntityKind } from './entities'
import { fmtDate, fmtText } from './format'
import type { Money } from './money'
import { fmtMoneyField, parseMoney, toNumber } from './money'

/** Область справочника полей для типа: `item:31`. */
export const smartEntity = (typeId: number | string) => `item:${typeId}`

/**
 * Карточка записи в Bitrix. Своих карточек смарт-процессов в CRM пока нет (только чтение
 * списков), поэтому из таблицы ведём в Bitrix — и подписываем это, чтобы не выглядело
 * как «внутренняя» ссылка.
 */
export const bitrixItemUrl = (typeId: number | string, id: string | number) =>
  `https://cp.cir.cx/crm/type/${typeId}/details/${id}/`

// ─────────────────────────── какие типы показываем ───────────────────────────

export interface SmartTypeSpec {
  typeId: number
  /** Кусок адреса: /smart/<path>. Человекочитаемый, чтобы ссылку можно было переслать. */
  path: string
  title: string
  /**
   * Одна строка под заголовком: что это и КТО ЭТО ПИШЕТ. Второе важнее первого —
   * почти все эти типы наполняют бизнес-процессы Bitrix, и «почему тут нельзя править»
   * должно быть видно на экране, а не только в голове у разработчика
   * (`БИЗНЕС-ПРОЦЕССЫ_Bitrix.md`, таблица смарт-процессов).
   */
  note: string
  /**
   * Показывать «Сумму» (стандартное поле `opportunity`) колонкой по умолчанию.
   * Не у всех типов она осмысленна: у счетов заполнена в 97 % записей и это главная цифра
   * раздела, у договоров и АВР суммы нет вовсе — пустая колонка только мешала бы.
   */
  money?: boolean
}

/**
 * Шестой тип добавляется строкой в этот список — при условии, что он залит в ядро.
 * Если не залит, ядро честно ответит «не умею», и экран это покажет (см. QueryState).
 */
export const SMART_TYPES: SmartTypeSpec[] = [
  {
    typeId: 31,
    path: 'invoices',
    money: true,
    title: 'Счета клиенту',
    note: 'Смарт-счета Bitrix — исходящие счета клиентам. Самый большой из перенесённых типов; '
      + 'сколько записей в ядре сейчас, видно в счётчике над таблицей.',
  },
  {
    typeId: 1160,
    path: 'payment-calendar',
    money: true,
    title: 'Счета на оплату',
    note: 'Платёжный календарь: это НАШИ оплаты поставщикам, а не дебиторка. Карточку создаёт '
      + 'бизнес-процесс списка 18, портал 55 дописывает компанию и сделку.',
  },
  {
    typeId: 153,
    path: 'contracts',
    title: 'Реестр договоров',
    note: 'Пишут только бизнес-процессы (10 своих шаблонов). Для людей это справочник: '
      + 'договор находят и выбирают, а не заводят руками.',
  },
  {
    typeId: 170,
    path: 'avr',
    title: 'База АВР',
    note: 'Наполняется бизнес-процессом из элементов списка 278 — напрямую в 170 не пишет никто. '
      + 'Зарегистрировать АВР = создать элемент 278 и дождаться номера в сделке.',
  },
  {
    typeId: 176,
    path: 'deal-reports',
    title: 'Отчёты по сделкам',
    note: 'Цепочка согласований (замер 28.07.2026: 24 шаблона бизнес-процессов: РОП, руководители сервиса и ремонта, '
      + 'проектный отдел), а не таблица. Здесь виден результат согласования, но не сам ход.',
  },
]

export const smartTypeByPath = (path?: string): SmartTypeSpec | undefined =>
  SMART_TYPES.find((t) => t.path === path)

// ─────────────────────────── колонки ───────────────────────────

export type ColKind =
  | 'id' | 'title' | 'stage' | 'category'
  | 'company' | 'contact' | 'deal' | 'user'
  | 'money' | 'moneyStd' | 'number' | 'date' | 'datetime' | 'bool' | 'enum' | 'crm' | 'text'

export interface SmartColumn {
  code: string
  kind: ColKind
  /** Заголовок. Из справочника, если поле там описано; иначе наше слово или сам код. */
  label: string
  /** Название пришло из справочника Bitrix, а не подставлено нами. */
  known: boolean
  multiple: boolean
  /** Значение считает бизнес-процесс: править нельзя даже когда появится запись. */
  bpComputed: boolean
  /** Системное поле смарт-процесса (не UF): у него своя колонка в ядре. */
  system: boolean
}

/** Числовые колонки выравниваем вправо — иначе суммы не сравнить глазами. */
export const isNumCol = (k: ColKind) => k === 'money' || k === 'moneyStd' || k === 'number'

/**
 * Системные поля crm.item.*. Их состав одинаков у всех типов и известен из ядра
 * (ITEM_COLS в core_api), поэтому единственные коды полей, которые тут зашиты, — эти.
 * `def` — колонка показывается по умолчанию.
 */
const SYSTEM: Array<{ code: string; kind: ColKind; label: string; def?: boolean }> = [
  { code: 'id', kind: 'id', label: 'ID', def: true },
  { code: 'title', kind: 'title', label: 'Название', def: true },
  { code: 'stageId', kind: 'stage', label: 'Стадия', def: true },
  { code: 'categoryId', kind: 'category', label: 'Воронка' },
  { code: 'companyId', kind: 'company', label: 'Компания', def: true },
  { code: 'contactId', kind: 'contact', label: 'Контакт' },
  { code: 'parentId2', kind: 'deal', label: 'Сделка', def: true },
  { code: 'assignedById', kind: 'user', label: 'Ответственный', def: true },
  { code: 'opportunity', kind: 'moneyStd', label: 'Сумма (стандартная)' },
  { code: 'createdTime', kind: 'datetime', label: 'Создана' },
  { code: 'updatedTime', kind: 'datetime', label: 'Изменена', def: true },
]

const SYSTEM_CODES = new Set(SYSTEM.map((s) => s.code))

/**
 * Как показывать системное поле в развёрнутой записи (там всё выводится текстом, без ссылок
 * и значков). Отдельно от колонок, потому что справочник Bitrix описывает `id` как integer —
 * и без этой поправки идентификатор счёта выглядел бы как «3 101» с разрядами.
 */
const SYSTEM_DETAIL_KIND: Record<string, ColKind> = {
  id: 'text', title: 'text', stageId: 'text', categoryId: 'text',
  companyId: 'text', contactId: 'text', parentId2: 'text', assignedById: 'text',
  opportunity: 'number', createdTime: 'datetime', updatedTime: 'datetime', currencyId: 'text',
}

/** Как показать поле в развёрнутой записи: поправка для системных, иначе тип из справочника. */
export const detailKind = (code: string, type?: string): ColKind =>
  SYSTEM_DETAIL_KIND[code] ?? kindOfFieldType(type)

/**
 * id привязки из записи смарт-процесса. Bitrix пишет «ничего не привязано» нулём, а не пустотой,
 * и без этой чистки в колонке «Сделка» появляется ссылка «#0» на несуществующую сделку.
 */
export const refId = (v: unknown): string => {
  const s = String(v ?? '').trim()
  return s === '0' || s === '' ? '' : s
}

/**
 * id судна из поля сделки «Судно» (`UF_CRM_1502613831`).
 *
 * ЗАЧЕМ ОТДЕЛЬНАЯ ФУНКЦИЯ, А НЕ ПРАВКА `refId`. В части сделок в этом поле лежит не число,
 * а привязка в форме `C_19795` — «контакт номер 19795». Так Bitrix записывает поле-привязку
 * к сущности, когда оно заведено «привязкой к CRM», а не числом; на 28.07.2026 таких сделок
 * в зеркале ровно 54 (`select count(*) from deals where raw->>'UF_CRM_1502613831' like 'C\_%'`,
 * например 101978 = `C_19795`). Приставка ЗАКОННА и только у этого поля: у сделки, компании,
 * контакта и записи смарт-процесса привязка всегда голое число, и снимать там «C_» было бы
 * порчей идентификатора. Поэтому `refId` остаётся общим и нетронутым.
 *
 * ЧТО БЫЛО СЛОМАНО. `EntityLink kind="vessel" id="C_19795"` собирал адрес `/vessels/C_19795`,
 * карточка судна брала этот текст из адреса и слала дальше как id контакта. Ядро с 28.07.2026
 * форму `C_NNN` в ФИЛЬТРЕ разбирает (проверено: `crm.deal.list` по `UF_CRM_1502613831=C_19795`
 * и по `19795` отдают одни и те же сделки), но адрес страницы от этого верным не становится:
 * в нём должен стоять тот же номер, что у карточки контакта-судна.
 *
 * Ноль чистится тем же `refId` — «судна нет» Bitrix пишет нулём, и здесь это те же три
 * разных ответа, что везде.
 */
export const vesselRefId = (v: unknown): string => {
  const s = refId(Array.isArray(v) ? v[0] : v)
  // Только `C_` и только перед цифрами: «C_ABC» — не привязка, и портить такое значение
  // молчаливым обрезанием нельзя, пусть уж будет видно как есть.
  const m = /^C_(\d+)$/.exec(s)
  return m ? refId(m[1]) : s
}

/**
 * Всегда просим у ядра: валюта нужна стандартной сумме, а системные поля дёшевы
 * (в отличие от сотни UF-полей, ради которых и существует явный select).
 */
export const SYSTEM_SELECT = [...SYSTEM.map((s) => s.code), 'currencyId']

/**
 * Служебная требуха Bitrix: поля есть у каждого смарт-процесса, смысла в списке не несут.
 * Из автоподбора колонок исключены, но в выборе колонок остаются — вдруг понадобятся.
 */
const NOISY = new Set([
  'entityTypeId', 'xmlId', 'webformId', 'observers', 'createdBy', 'updatedBy', 'movedBy',
  'movedTime', 'lastActivityBy', 'lastActivityTime', 'sourceId', 'sourceDescription',
  'stageSemanticId', 'isManualOpportunity', 'opened', 'taxValue', 'isRecurring', 'locationId',
  'utmSource', 'utmMedium', 'utmCampaign', 'utmContent', 'utmTerm', 'currencyId',
  'previousStageId', 'begindate_ts', 'parentId', 'mycompanyId',
  // Коммуникации Bitrix ведёт на сделке и контакте, а не на смарт-процессе: эти пять полей
  // пусты у всех 31 467 счетов (на 28.07.2026). По типу (datetime) автоподбор считал их
  // лучшими колонками и вытеснял ими сумму, дату выставления и срок оплаты.
  'lastCommunicationTime', 'lastCommunicationCallTime', 'lastCommunicationEmailTime',
  'lastCommunicationImolTime', 'lastCommunicationWebformTime',
  // Внутренняя механика Bitrix. Заполнена всегда — тем и опасна: выглядит как «полезное поле».
  'ufCrmOrderIds', 'ufCrmLeadId', 'ufCrmIblockId', 'ufCrmActive', 'ufCrmBpPublished',
  'ufCrmDateCreate', 'ufCrmDateModify', 'ufCrmPropertyData', 'ufCrmSectionId', 'ufCrmSort',
])

/** Тип поля Bitrix → как его показывать. Неизвестный тип — текстом, это всегда безопасно. */
export function kindOfFieldType(type?: string): ColKind {
  switch ((type || '').toLowerCase()) {
    case 'money': return 'money'
    case 'double': case 'integer': case 'int': return 'number'
    case 'date': return 'date'
    case 'datetime': return 'datetime'
    case 'boolean': case 'char': return 'bool'
    case 'enumeration': case 'crm_status': case 'list': return 'enum'
    case 'employee': case 'user': return 'user'
    case 'crm': case 'crm_entity': return 'crm'
    default: return 'text'
  }
}

/**
 * Годится ли поле в интерфейс по ДАННЫМ, а не по типу. Правило одно и для автоподбора
 * колонок, и для списка сортировок — иначе поле, отсеянное из колонок как пустое, всё равно
 * предлагалось бы сортировкой.
 *
 * Тип поля обманчив: «Дата последнего звонка» у счетов — datetime, выглядит полезной,
 * а непустых значений ноль во всех 31 467 записях (на 28.07.2026). И наоборот, «Есть
 * отчет ОП (не используется)» заполнено в 100 % отчётов — одним и тем же словом «нет».
 * Поэтому условий два: непусто И значения различаются.
 *
 * Статистики может не быть (`sync.py fields-stats` ещё не отработал, старое ядро) — тогда
 * пропускаем поле дальше. Спрятать колонку из-за отсутствия данных о ней хуже, чем показать
 * лишнюю: пустое место человек объяснить не сможет.
 */
export function информативноПоМете(m: FieldMeta | undefined): boolean {
  if (m?.fill === undefined) return true
  return m.fill > 0 && (m.distinct === undefined || m.distinct > 1)
}

function column(code: string, kind: ColKind, label: string, meta: FieldMeta | undefined,
                system: boolean): SmartColumn {
  // Ядро подставляет код вместо пустого названия — это и значит «поле в Bitrix не назвали».
  const fromDict = meta?.title && meta.title !== code ? meta.title : ''
  return {
    code,
    kind,
    label: fromDict || label,
    known: !!fromDict,
    multiple: !!meta?.multiple,
    bpComputed: !!meta?.bp_computed,
    system,
  }
}

export interface SmartColumns {
  /** Все колонки, которые можно показать: системные, затем поля типа в порядке Bitrix. */
  all: SmartColumn[]
  /** Что показываем, пока пользователь не выбрал сам. */
  defaults: string[]
  byCode: Map<string, SmartColumn>
}

/**
 * Собрать колонки типа из справочника полей.
 *
 * Автоподбор нарочно скромный: 5 полей типа «деньги/дата/перечисление/число», в том порядке,
 * в каком их показывает сам Bitrix (поле `sort`). Показать все — это сорок колонок с
 * горизонтальной прокруткой; угадать «главные» по названию нельзя, а притвориться, что угадали,
 * хуже, чем дать выбрать. Остальные поля доступны в «Колонки».
 */
export function buildColumns(f: FieldsApi, spec?: { money?: boolean }): SmartColumns {
  const all: SmartColumn[] = SYSTEM.map((s) => column(s.code, s.kind, s.label, f.meta(s.code), true))
  const defaults = SYSTEM.filter((s) => s.def || (spec?.money && s.code === 'opportunity'))
    .map((s) => s.code)

  const custom: SmartColumn[] = []
  for (const { code, meta } of f.list) {
    if (SYSTEM_CODES.has(code)) continue
    custom.push(column(code, kindOfFieldType(meta.type), code, meta, false))
  }
  all.push(...custom)

  // порядок предпочтения: сначала то, что несёт цифру или срок, потом признаки
  const RANK: Partial<Record<ColKind, number>> = { money: 0, date: 1, datetime: 2, number: 3, enum: 4, bool: 5 }
  const информативно = (c: SmartColumn) => информативноПоМете(f.meta(c.code))
  const auto = custom
    .filter((c) => !NOISY.has(c.code) && RANK[c.kind] !== undefined && информативно(c))
    // Заполненность важнее типа: срок оплаты, заполненный в 90 % счетов, полезнее суммы
    // налога, заполненной в двух. При равной заполненности решает тип.
    .sort((a, b) => (f.meta(b.code)?.fill ?? 0) - (f.meta(a.code)?.fill ?? 0)
                    || (RANK[a.kind] ?? 9) - (RANK[b.kind] ?? 9))
    .slice(0, 5)
  // Ничего «цифрового» у типа нет (так выглядит справочник договоров или отчётов) —
  // тогда лучше пара текстовых полей, чем одни системные колонки.
  const fallback = custom
    .filter((c) => !NOISY.has(c.code) && c.kind === 'text' && информативно(c))
    .sort((a, b) => (f.meta(b.code)?.fill ?? 0) - (f.meta(a.code)?.fill ?? 0))
    .slice(0, 3)
  defaults.push(...(auto.length ? auto : fallback).map((c) => c.code))

  return { all, defaults, byCode: new Map(all.map((c) => [c.code, c])) }
}

// ─────────────────────────── значения ───────────────────────────

/** Ссылка на сущность CRM из crm-поля Bitrix: «CO_77» → компания 77. */
export function parseCrmRef(v: unknown): { kind: EntityKind; id: string } | null {
  const s = String(v ?? '').trim()
  const m = /^(CO|C|D|L)_(\d+)$/i.exec(s)
  if (!m) return null
  const id = m[2]
  const p = m[1].toUpperCase()
  if (p === 'CO') return { kind: 'company', id }
  if (p === 'C') return { kind: 'contact', id }
  if (p === 'D') return { kind: 'deal', id }
  return null   // лид — своего раздела в CRM нет, показываем текстом
}

const boolText = (v: unknown): string => {
  const s = String(v ?? '').trim().toUpperCase()
  if (s === 'Y' || s === 'TRUE' || s === '1') return 'да'
  if (s === 'N' || s === 'FALSE' || s === '0') return 'нет'
  return s || '—'
}

/** Одно значение поля текстом. Множественные поля разбираются уровнем выше. */
function oneValue(kind: ColKind, code: string, v: unknown, f: FieldsApi): string {
  if (v === null || v === undefined || v === '') return '—'
  switch (kind) {
    case 'money': return fmtMoneyField(v)
    // Стандартная сумма (`opportunity`) — просто число: валюта у неё лежит в СОСЕДНЕМ поле
    // записи (`currencyId`), а сюда приходит одно значение. Дорисовать «₽» отсюда значило бы
    // угадать валюту, поэтому показываем числом с разрядами. Без этой ветки она попадала
    // в default и выводилась как «95000» — сплошным числом, которое не прочитать глазами.
    case 'moneyStd':
    case 'number': {
      const n = toNumber(v)
      return n === null ? String(v) : n.toLocaleString('ru-RU', { maximumFractionDigits: 2 })
    }
    case 'date': return fmtDate(String(v))
    case 'datetime': return fmtDate(String(v), true)
    case 'bool': return boolText(v)
    case 'enum': return f.enumText(code, v) || '—'
    default: return fmtText(v)
  }
}

/**
 * Значение ячейки текстом. Множественное поле Bitrix отдаёт массивом — склеиваем,
 * иначе в таблице появляется «[object Object]».
 */
export function cellText(col: SmartColumn, v: unknown, f: FieldsApi): string {
  if (Array.isArray(v)) {
    const parts = v.map((x) => oneValue(col.kind, col.code, x, f)).filter((s) => s && s !== '—')
    return parts.length ? parts.join(', ') : '—'
  }
  return oneValue(col.kind, col.code, v, f)
}

// ─────────────────────────── итоги по деньгам ───────────────────────────

export interface MoneyTotal {
  code: string
  label: string
  currency: string
  sum: number
  /** По скольким строкам страницы посчитано — пустые значения в счёт не идут. */
  count: number
}

/**
 * Суммы по денежным колонкам ЗАГРУЖЕННОЙ страницы.
 *
 * Ядро агрегатов не умеет и отдаёт по 50 записей: при 31 467 счетах (на 28.07.2026) «итог
 * по разделу» — это 630 запросов. Поэтому считаем по странице и подписываем это на экране,
 * как в дебиторке.
 * Валюты не смешиваем: «515000|RUB» и «6000|EUR» в одну сумму складывать нельзя.
 */
export function moneyTotals(cols: SmartColumn[], rows: CrmItem[]): MoneyTotal[] {
  const out: MoneyTotal[] = []
  for (const col of cols) {
    if (col.kind !== 'money' && col.kind !== 'moneyStd') continue
    const byCur = new Map<string, { sum: number; count: number }>()
    for (const r of rows) {
      const raw = r[col.code]
      const values = Array.isArray(raw) ? raw : [raw]
      for (const v of values) {
        const m: Money | null = col.kind === 'moneyStd'
          // у стандартной суммы валюта в отдельном поле — это не money-строка Bitrix
          ? (() => {
              const n = toNumber(v)
              return n === null || n === 0 ? null : { amount: n, currency: String(r.currencyId || 'RUB') }
            })()
          : parseMoney(v)
        if (!m) continue
        const acc = byCur.get(m.currency) || { sum: 0, count: 0 }
        acc.sum += m.amount
        acc.count += 1
        byCur.set(m.currency, acc)
      }
    }
    for (const [currency, acc] of byCur) {
      out.push({ code: col.code, label: col.label, currency, sum: acc.sum, count: acc.count })
    }
  }
  return out
}

// ─────────────────────────── фильтр по произвольному полю ───────────────────────────

/**
 * Что ядро реально умеет по конкретному полю — и, главное, чего оно НЕ умеет молча.
 *
 * Диапазон (`>=`/`<=`) ядро считает по `raw->>'поле'` с приведением: число сравнивает числом,
 * ISO-дату — датой, а всё остальное отказывается сравнивать (`build_where` в core_api).
 * Money-поле приходит строкой «515000|RUB» — она не число и не дата, поэтому диапазон по
 * деньгам ядро либо отвергнет, либо (что хуже) не найдёт ни одной строки. Не предлагаем его
 * вовсе: пустой список выглядит как «таких счетов нет», а это неправда.
 */
export type FilterMode = 'contains' | 'eq' | 'range' | 'filled' | 'empty'

export function filterModes(kind: ColKind): FilterMode[] {
  switch (kind) {
    case 'enum': case 'bool': return ['eq', 'filled', 'empty']
    case 'date': case 'datetime': case 'number': return ['range', 'filled', 'empty']
    // деньги — только «заполнено/пусто» и подстрока: см. комментарий выше
    case 'money': return ['contains', 'filled', 'empty']
    default: return ['contains', 'eq', 'filled', 'empty']
  }
}

export const FILTER_MODE_LABEL: Record<FilterMode, string> = {
  contains: 'содержит',
  eq: 'равно',
  range: 'в диапазоне',
  filled: 'заполнено',
  empty: 'не заполнено',
}

/** Собрать кусок фильтра для ядра. Пустые значения не отправляем — это «нет условия». */
export function fieldFilter(code: string, mode: FilterMode, v1: string, v2: string): Record<string, unknown> {
  if (!code) return {}
  if (mode === 'filled') return { [`!${code}`]: '' }
  if (mode === 'empty') return { [code]: '' }
  if (mode === 'contains') return v1 ? { [`%${code}`]: v1 } : {}
  if (mode === 'eq') return v1 ? { [code]: v1 } : {}
  const out: Record<string, unknown> = {}
  if (v1) out[`>=${code}`] = v1
  if (v2) out[`<=${code}`] = v2
  return out
}

/**
 * Поля, по которым осмысленно сортировать.
 *
 * Системные — только те, у кого порядок что-то значит: сортировать по companyId — это
 * сортировать по внутреннему номеру компании, а не по её названию.
 *
 * Из полей типа берём только даты. UF-поле ядро сортирует как ТЕКСТ (`raw->>'поле'`):
 * для ISO-даты это совпадает с сортировкой по дате, а для числа и денег — нет («9» окажется
 * больше «10»). Молча неверный порядок хуже, чем его отсутствие.
 */
const SORTABLE_SYSTEM = new Set(['id', 'title', 'opportunity', 'createdTime', 'updatedTime'])

/**
 * `f` — справочник полей. Он не обязателен (без него остаётся отбор по типу), но со ним
 * из списка уходят поля, пустые во всех записях: у счетов это четыре `lastCommunication*` —
 * коммуникации Bitrix ведёт на сделке, и сортировка по ним не меняет порядок вообще.
 * Не ошибка, но бессмысленный выбор: в подборе колонок эти поля уже отсеяны по заполненности,
 * и в сортировке действует то же правило, а не своё.
 */
export function sortableColumns(cols: SmartColumn[], f?: Pick<FieldsApi, 'meta'>): SmartColumn[] {
  return cols.filter((c) => {
    if (c.system) return SORTABLE_SYSTEM.has(c.code)
    if (c.kind !== 'date' && c.kind !== 'datetime') return false
    if (NOISY.has(c.code)) return false
    return f ? информативноПоМете(f.meta(c.code)) : true
  })
}
